import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import ProcessDiagram from "../components/ProcessDiagram";
import HubspotPlaceholder from "../components/HubspotPlaceholder";
import { HouseHeart } from "../components/Illustrations";
import { ShieldCheck, Timer, Users, Target } from "lucide-react";

const EMPLOYER_STEPS = [
  { overline: "Step 01", title: "Discovery briefing", body: "We sit down with the hiring manager and the team. We want to understand the role, the culture, the stretch and the non-negotiables." },
  { overline: "Step 02", title: "Targeted search", body: "We map the market, work our network, and approach passive candidates discreetly. No mass mail-outs, no shortcuts." },
  { overline: "Step 03", title: "Tight shortlist", body: "Three to five candidates we'd genuinely hire ourselves — each with detailed write-ups, references begun, and prep done." },
  { overline: "Step 04", title: "Offer & onboard", body: "We manage offer dynamics, counter-offers and notice periods. Then we check in at 30, 60 and 90 days post-start." },
];

const SERVICES = [
  { icon: Target, title: "Permanent search", body: "Senior individual contributors through to executive hires across NZ." },
  { icon: Timer, title: "Contract & interim", body: "Day-rate and fixed-term cover when you need someone moving in a week." },
  { icon: Users, title: "Team build-outs", body: "Scaling a function? We'll plan, sequence and resource the whole hiring wave." },
  { icon: ShieldCheck, title: "Retained executive", body: "Confidential, partner-led search for board, GM and C-suite roles." },
];

export default function FindACandidate() {
  return (
    <div data-testid="page-find-a-candidate">
      <Seo
        title="Find a Candidate"
        description="Boutique, partner-led technology recruitment for NZ businesses who'd rather see three excellent candidates than thirty average ones."
        image="/og-brand.png"
        path="/find-a-candidate"
      />
      <PageHero
        overline="For employers"
        title="Find the hire your team actually needs."
        accentWord="needs"
        subtitle="Boutique, partner-led technology recruitment for businesses who'd rather see three excellent candidates than thirty average ones."
      />

      <section className="py-20 md:py-28 bg-cream" data-testid="employer-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">How we find your next hire</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              A process you'll actually enjoy being part of.
            </h2>
            <p className="mt-6 font-body text-ink-700 leading-relaxed">
              Senior consultants only. One point of contact. Honest updates,
              even when there's nothing new to report.
            </p>
            <div className="mt-10 bg-ink text-cream rounded-2xl p-6 flex items-center gap-4 relative overflow-hidden">
              <HouseHeart className="absolute -right-2 -bottom-2 w-40 h-20 opacity-60" stroke="#C2FFE1" strokeWidth={4} />
              <p className="font-headings text-xl text-mint leading-tight relative">
                Search, brief, shortlist, place — handled.
              </p>
            </div>
          </div>
          <div className="lg:col-span-8">
            <ProcessDiagram steps={EMPLOYER_STEPS} accent="mint" />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-mint-soft" data-testid="employer-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">What we do</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              Four ways we can help you hire.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  data-testid={`service-${i}`}
                  className="bg-white p-8 md:p-10 rounded-2xl border border-ink-100"
                >
                  <Icon size={28} className="text-stone-dark mb-5" strokeWidth={1.5} />
                  <h3 className="font-headings text-2xl text-ink mb-3 leading-tight">{s.title}</h3>
                  <p className="font-body text-ink-700 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-cream" data-testid="employer-booking">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-dark font-medium mb-5">Ready to brief us?</p>
            <h2 className="font-headings text-4xl md:text-5xl text-ink tracking-tight leading-tight">
              Book a 30-minute discovery call.
            </h2>
            <p className="mt-6 font-body text-ink-700 max-w-2xl mx-auto leading-relaxed">
              Tell us about the role. We'll tell you honestly whether we can
              help, what the market looks like, and what good will cost.
            </p>
          </div>
          <HubspotPlaceholder formType="employer" />
        </div>
      </section>
    </div>
  );
}
